import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Tooltip } from '../Tooltip/Tooltip';
import styles from './Pagination.module.css';

export interface PaginationProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  total?: number;
  pageSize?: number;
  siblingCount?: number;
  showEdges?: boolean;
  compact?: boolean;
  itemsLabel?: string;
  className?: string;
}

const DOTS = 'dots' as const;

function buildRange(page: number, totalPages: number, siblingCount: number): (number | typeof DOTS)[] {
  const windowSize = siblingCount * 2 + 5;
  if (totalPages <= windowSize) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const left = Math.max(page - siblingCount, 1);
  const right = Math.min(page + siblingCount, totalPages);
  const showLeftDots = left > 3;
  const showRightDots = right < totalPages - 2;

  if (!showLeftDots) {
    const head = 3 + siblingCount * 2;
    return [...Array.from({ length: head }, (_, i) => i + 1), DOTS, totalPages];
  }
  if (!showRightDots) {
    const tail = 3 + siblingCount * 2;
    return [1, DOTS, ...Array.from({ length: tail }, (_, i) => totalPages - tail + 1 + i)];
  }
  return [
    1,
    DOTS,
    ...Array.from({ length: right - left + 1 }, (_, i) => left + i),
    DOTS,
    totalPages,
  ];
}

/**
 * Единая пагинация списков. Раньше каждая страница рисовала свою: у одной были
 * только «Назад/Вперёд» без номеров, у другой окно номеров съезжало у края.
 *
 * @use постраничная навигация под списком, с подписью «21–40 из 137».
 */
export const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  onChange,
  total,
  pageSize,
  siblingCount = 1,
  showEdges = true,
  compact = false,
  itemsLabel = 'элементов',
  className,
}) => {
  if (totalPages <= 1) return null;

  const current = Math.min(Math.max(page, 1), totalPages);
  const go = (next: number) => {
    const clamped = Math.min(Math.max(next, 1), totalPages);
    if (clamped !== current) onChange(clamped);
  };

  const from = pageSize ? (current - 1) * pageSize + 1 : null;
  const to = pageSize && total != null ? Math.min(current * pageSize, total) : null;

  return (
    <nav className={cn(styles.root, className)} aria-label={`Пагинация: ${itemsLabel}`}>
      <span className={styles.info} aria-live="polite">
        {from != null && to != null && total != null ? (
          <>
            {from}–{to} из {total}
          </>
        ) : (
          <>
            Страница {current} из {totalPages}
          </>
        )}
      </span>

      <div className={styles.buttons}>
        {showEdges && !compact && (
          <Tooltip content="В начало" disabled={current === 1}>
            <button
              type="button"
              className={cn(styles.btn, styles.edge)}
              onClick={() => go(1)}
              disabled={current === 1}
              aria-label="Первая страница"
            >
              <ChevronsLeft size={16} aria-hidden />
            </button>
          </Tooltip>
        )}

        <Tooltip content="Предыдущая страница" disabled={current === 1}>
          <button
            type="button"
            className={styles.btn}
            onClick={() => go(current - 1)}
            disabled={current === 1}
            aria-label="Предыдущая страница"
          >
            <ChevronLeft size={16} aria-hidden />
          </button>
        </Tooltip>

        {compact ? (
          <span className={styles.compactValue}>
            {current} / {totalPages}
          </span>
        ) : (
          buildRange(current, totalPages, siblingCount).map((item, i) =>
            item === DOTS ? (
              <span key={`dots-${i}`} className={styles.dots} aria-hidden>
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={cn(styles.btn, styles.pageNum, item === current && styles.btnActive)}
                onClick={() => go(item)}
                aria-label={`Страница ${item}`}
                aria-current={item === current ? 'page' : undefined}
              >
                {item}
              </button>
            ),
          )
        )}

        <Tooltip content="Следующая страница" disabled={current === totalPages}>
          <button
            type="button"
            className={styles.btn}
            onClick={() => go(current + 1)}
            disabled={current === totalPages}
            aria-label="Следующая страница"
          >
            <ChevronRight size={16} aria-hidden />
          </button>
        </Tooltip>

        {showEdges && !compact && (
          <Tooltip content="В конец" disabled={current === totalPages}>
            <button
              type="button"
              className={cn(styles.btn, styles.edge)}
              onClick={() => go(totalPages)}
              disabled={current === totalPages}
              aria-label="Последняя страница"
            >
              <ChevronsRight size={16} aria-hidden />
            </button>
          </Tooltip>
        )}
      </div>
    </nav>
  );
};
